import React, { useEffect, useMemo, useState } from "react"
import { KButton, KSpan, KTitleSpan } from "kahuna-base-react-components"
import { toast } from "react-toastify"
import { marketingMilestoneRules } from "../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../constants"
import { MarketingMilestoneRule } from "../../../../types"
import { mapToCamelCase } from "../../../../utility"

type Props = {
  rules: MarketingMilestoneRule[]
  onClose: () => void
  onChanged: () => void
}

const formatLabel = (value?: string | null) => {
  if (!value) return "-"
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

const MarketingMilestoneRulesDrawer = ({ rules, onClose, onChanged }: Props) => {
  const [drafts, setDrafts] = useState<{ [id: number]: MarketingMilestoneRule }>({})
  const [savingId, setSavingId] = useState<number | null>(null)

  useEffect(() => {
    const next: { [id: number]: MarketingMilestoneRule } = {}
    rules.forEach((rule) => {
      next[rule.id] = { ...rule, config: { ...rule.config } }
    })
    setDrafts(next)
  }, [rules])

  const orderedRules = useMemo(() => Object.values(drafts).sort((a, b) => a.priority - b.priority), [drafts])

  const updateDraft = (id: number, patch: Partial<MarketingMilestoneRule>) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))
  }

  const updateConfig = (id: number, key: string, value: string) => {
    setDrafts((prev) => {
      const current = prev[id]
      const config = { ...current.config }
      const trimmed = value.trim()
      if (trimmed === "") {
        delete config[key]
      } else if (
        key === "thresholds" ||
        key === "position_thresholds" ||
        key === "spotify_thresholds" ||
        key === "youtube_thresholds"
      ) {
        config[key] = trimmed
          .split(",")
          .map((part) => Number(part.trim()))
          .filter((part) => !Number.isNaN(part))
      } else if (Number.isNaN(Number(trimmed))) {
        config[key] = trimmed
      } else {
        config[key] = Number(trimmed)
      }
      return { ...prev, [id]: { ...current, config } }
    })
  }

  const saveRule = (id: number) => {
    const draft = drafts[id]
    if (!draft) return
    const payload = {
      event_type: draft.eventType,
      name: draft.name,
      platform: draft.platform,
      country: draft.country,
      playlist_type: draft.playlistType,
      config: draft.config,
      active: draft.active,
      notify_internal: draft.notifyInternal,
      notify_slack: draft.notifySlack,
      priority: draft.priority
    }
    setSavingId(id)
    marketingMilestoneRules(`/${id}/`, "patch", payload)
      .then((resp) => {
        const updated = mapToCamelCase<MarketingMilestoneRule>(resp.data)
        // keep config's snake_case keys intact (do not use the camelCased copy)
        setDrafts((prev) => ({ ...prev, [id]: { ...updated, config: { ...resp.data.config } } }))
        toast.success("Rule saved", successFilledToastOptions)
        setSavingId(null)
        onChanged()
      })
      .catch((err) => {
        setSavingId(null)
        toast.error(err?.response?.data?.detail || "Could not save rule", errorFilledToastOptions)
      })
  }

  const renderConfigField = (rule: MarketingMilestoneRule, key: string) => {
    const value = rule.config?.[key]
    const display = Array.isArray(value) ? value.join(", ") : value === undefined ? "" : `${value}`
    return (
      <label key={key} className="flex flex-col gap-1 min-w-[120px]">
        <span className="text-[11px] uppercase tracking-wide text-gray-400">{formatLabel(key)}</span>
        <input
          className="h-8 border border-gray-200 rounded px-2 text-xs"
          value={display}
          onChange={(e) => updateConfig(rule.id, key, e.target.value)}
        />
      </label>
    )
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-[640px] max-w-full h-full bg-white shadow-xl overflow-y-auto z-50">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <KTitleSpan text="Milestone Rules" fontSize={18} color="#111" lineHeight="24px" fontWeight={600} />
          <KButton text="Close" height="32px" width="80px" background="#F5F5F5" onClick={onClose} />
        </div>
        <div className="p-5 grid gap-4">
          {orderedRules.length === 0 && (
            <KSpan
              text="No rules yet — click Seed Rules from the toolbar to create defaults."
              color="#666"
              fontSize={13}
            />
          )}
          {orderedRules.map((rule) => {
            const dirty = JSON.stringify(rule) !== JSON.stringify(rules.find((r) => r.id === rule.id))
            const configKeys = Object.keys(rule.config || {})
            const isTotal = rule.eventType === "track_total" || rule.eventType === "artist_total"
            const meta = rule as unknown as { sourceCron?: string; frequency?: string }
            return (
              <div key={rule.id} className="border border-gray-100 rounded-xl p-4 bg-[#FCFCFC]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{rule.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500">{formatLabel(rule.eventType)}</span>
                      {meta.sourceCron && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                            meta.frequency === "weekly"
                              ? "bg-violet-50 text-violet-700 border-violet-100"
                              : "bg-sky-50 text-sky-700 border-sky-100"
                          }`}
                          title="Which cron generates this rule and how often"
                        >
                          {meta.sourceCron} · {meta.frequency === "weekly" ? "Weekly" : "Daily"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <label className="flex items-center gap-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={rule.active}
                        onChange={(e) => updateDraft(rule.id, { active: e.target.checked })}
                      />
                      Active
                    </label>
                    <label className="flex items-center gap-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={rule.notifyInternal}
                        onChange={(e) => updateDraft(rule.id, { notifyInternal: e.target.checked })}
                      />
                      Internal
                    </label>
                    <label className="flex items-center gap-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={rule.notifySlack}
                        onChange={(e) => updateDraft(rule.id, { notifySlack: e.target.checked })}
                      />
                      Slack
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mt-3">
                  {!isTotal && (
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] uppercase tracking-wide text-gray-400">Platform</span>
                      <input
                        className="h-8 border border-gray-200 rounded px-2 text-xs"
                        value={rule.platform || ""}
                        onChange={(e) => updateDraft(rule.id, { platform: e.target.value || null })}
                      />
                    </label>
                  )}
                  {!isTotal && (
                    <label className="flex flex-col gap-1">
                      <span className="text-[11px] uppercase tracking-wide text-gray-400">Country</span>
                      <input
                        className="h-8 border border-gray-200 rounded px-2 text-xs"
                        value={rule.country || ""}
                        onChange={(e) => updateDraft(rule.id, { country: e.target.value || null })}
                      />
                    </label>
                  )}
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-wide text-gray-400">Priority</span>
                    <input
                      type="number"
                      className="h-8 border border-gray-200 rounded px-2 text-xs"
                      value={rule.priority}
                      onChange={(e) => updateDraft(rule.id, { priority: Number(e.target.value) })}
                    />
                  </label>
                </div>

                {configKeys.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {configKeys.map((key) => renderConfigField(rule, key))}
                  </div>
                )}

                <div className="flex justify-end mt-3">
                  <KButton
                    text={savingId === rule.id ? "Saving..." : "Save"}
                    height="32px"
                    width="100px"
                    background={dirty ? "#111" : "#E5E5E5"}
                    hoverBackground={dirty ? "#333" : "#E5E5E5"}
                    textColor={dirty ? "#FFF" : "#999"}
                    disabled={!dirty || savingId === rule.id}
                    onClick={() => saveRule(rule.id)}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default MarketingMilestoneRulesDrawer
